// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import { Fade, TransitionsOptions, alpha, createTheme } from "@mui/material";

export const colorsv5 = {
  // DON'T use #C12B20, use primaryAccent color instead
  primary: '#000',
  // primaryAccent: '#D10C0C',
  primaryAccent: '#0870D8',
  // primaryAccentHover: '#DA1616',
  primaryAccentHover: '#0870D8',
  secondary: '#373633',
  red: '#DC3545',
  warning: '#FFC107',
  error: '#DC3545',
  black: '#000',
  black2: '#141414',
  grey: '#A9AAAC',
  greyLight: '#DFDFDF',
  greyLight2: '#C4C4C4',
  greyLight3: '#6C757D',
  greyLight4: '#E2E2E2',
  greyLight5: '#F8F9FA',
  greyLight6: '#D7D8D9',
  greyLight7: '#F1F1F1',
  greyDark: '#364859',
  greyDark2: '#8C8C8C',
  blueDark: '#071C4B',
  blue: '#236BF6',
  green: '#5FC97B',
  greenHover: '#6FCE88',
  white: '#FFFFFF',
  whitePale: '#FAFAFA',
  disabled: '#EFEFEF',
  // Colors for ordersummary graph.
  normal: '#99FF99',
  warning2: '#FFCC66',
  critical: '#FF9933',
  overdue: '#FF0000'
};

export const borderStylesv5 = {
  primarySolid: `1px solid ${alpha('#000', 0.3)}`,
  primarySolidHover: '1px solid transparent',
  secondarySolid: '',
  primaryDashed: `1px dashed ${alpha('#000', 0.3)}`,
  primaryDashedHover: '1px dashed transparent',
  secondaryDashed: ''
};

const transitionsv5: TransitionsOptions = {
  duration: {
    standard: 0.3
  }
};

export const opacityv5 = {
  disabled: .6,
};

const commonBorderStyles = {
  color: colorsv5.black,
  transition: `${transitionsv5.duration?.standard}s`,
  fontWeight: 500,
  cursor: 'pointer',
  lineHeight: 1.2,
};

export const shortcutsv5 = {
  borderPrimarySolid: {
    ...commonBorderStyles,
    borderBottom: borderStylesv5.primarySolid,

    '&:hover': {
      borderBottom: borderStylesv5.primarySolidHover
    }
  },
  borderPrimaryDashed: {
    ...commonBorderStyles,
    borderBottom: borderStylesv5.primaryDashed,

    '&:hover': {
      borderBottom: borderStylesv5.primaryDashedHover
    }
  },
  disabled: {
    opacity: .6,
    pointerEvents: 'none' as const,
  },
  errorText: {
    fontSize: 11,
    fontWeight: 500,
    width: '100%',
    color: colorsv5.error,
    position: 'relative' as const,
    paddingBottom: 8
  }
};

const fontFamily = '"Poppins", sans-serif';

const controlsRadius = 90;
const spacing = 4;
const containersRadius = 4;
const circleRadius = '50%';

export const scrollStyles = {
  '&::-webkit-scrollbar': {
    width: 8,
    height: 8
  },

  '&::-webkit-scrollbar-track': {
    borderRadius: containersRadius,
    backgroundColor: colorsv5.greyLight7,
    margin: 1
  },

  '&::-webkit-scrollbar-thumb': {
    background: colorsv5.greyLight3,
    borderRadius: containersRadius
  },

  '&::-webkit-scrollbar-thumb:hover': {
    background: colorsv5.greyDark
  },

  scrollbarColor: `${colorsv5.greyLight3} ${colorsv5.greyLight7}`,
  scrollbarWidth: 'thin' as const, // 'auto' | 'thin' | 'none' | 'initial' | 'inherit' but need 'const' to avoid linting error.
};

export default createTheme ({
  spacing: spacing,
  opacity: opacityv5,
  shortcuts: shortcutsv5,
  transitions: transitionsv5,
  borderStyles: borderStylesv5,
  radius: {
    control: controlsRadius,
    container: containersRadius,
    circle: circleRadius
  },
  palette: {
    colors: colorsv5,
    primary: {
      main: colorsv5.black
    },
    secondary: {
      main: colorsv5.secondary
    },
    error: {
      main: colorsv5.error
    }
  },
  typography: {
    fontFamily
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: controlsRadius,
          fontWeight: 500,
          textTransform: 'none',
          boxShadow: 'none',
  
          '&:hover': {
            opacity: 0.8
          },
  
          '&$disabled': {
            opacity: 0.6
          }
        },
        // label: {
        //   fontWeight: 500
        // },
        sizeSmall: {
          height: 36,
          padding: `0 ${spacing * 4}px`,
          fontSize: 12
        },
        sizeLarge: {
          height: 48,
          padding: `0 ${spacing * 6}px`,
          fontSize: 16
        },
        contained: {
          boxShadow: 'none',
          color: colorsv5.white,
  
          '&:hover': {
            boxShadow: 'none',
            backgroundColor: colorsv5.greyLight4
          },
  
          '&$disabled': {
            color: alpha(colorsv5.greyDark, 0.5),
            backgroundColor: colorsv5.disabled
          }
        },
        containedPrimary: {
          backgroundColor: colorsv5.primaryAccent,
  
          '&:hover': {
            backgroundColor: colorsv5.primaryAccentHover
          },
  
          '&$disabled': {
            color: colorsv5.whitePale,
            backgroundColor: colorsv5.primaryAccent,
            opacity: 0.6
          }
        },
        outlined: {
          borderColor: colorsv5.black
        },
        outlinedPrimary: {
          borderColor: colorsv5.blue,
          color: colorsv5.blue,
  
          '&:hover': {
            backgroundColor: colorsv5.primaryAccentHover,
            borderColor: colorsv5.primaryAccentHover,
            color: 'white'
          },
  
          '&$disabled': {
            borderColor: colorsv5.blue,
            color: colorsv5.blue,
            opacity: 0.6
          }
        },
        containedSecondary: {
          backgroundColor: colorsv5.green,
  
          '&:hover': {
            backgroundColor: colorsv5.greenHover
          },
  
          '&$disabled': {
            color: colorsv5.whitePale,
            backgroundColor: colorsv5.green,
            opacity: 0.6
          }
        }
      }
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      }
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      }
    },
    MuiTab: {
      defaultProps: {
        disableRipple: true,
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          position: 'relative',
  
          '& p': {
            // helper text
            marginTop: 0,
            position: 'absolute',
            top: '108%'
          }
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover $notchedOutline': {
            borderColor: colorsv5.black
          },
          '&$focused $notchedOutline': {
            borderColor: colorsv5.black,
            borderWidth: 1
          }
        },
        focused: {},
        notchedOutline: {
          top: 0,
          borderRadius: controlsRadius,
          borderColor: colorsv5.black,
          backgroundColor: '#fff',
  
          '& legend': {
            display: 'none'
          }
        },
        input: {
          paddingTop: 0,
          paddingLeft: spacing * 5,
          paddingBottom: 0,
          height: 48
        },
        inputSizeSmall: {
          paddingTop: 0,
          paddingBottom: 0,
          height: 36
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colorsv5.black}`,
  
          '&$error': {
            borderColor: colorsv5.error,
          }
        },
        input: {
          padding: 0,
          height: '100%',
  
          '&::before, &::after': {
            borderBottom: 'none !important' 
          },
        },
        underline: {
          '&::before, &::after': {
            borderBottom: 'none !important' 
          },
          
          '&$disabled:before': {
            borderBottomStyle: 'solid',
            borderColor: colorsv5.greyLight
          }
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: 48,
          fontSize: 16,
  
          '&$disabled': {
            color: '#000',
            pointerEvents: 'none'
          }
        },
        multiline: {
          height: 'auto'
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: 11,
          color: colorsv5.black
        }  
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          zIndex: 1,
  
          '&:focus': {
            backgroundColor: 'transparent'
          }
        },
        icon: {
          zIndex: 1
        } 
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: colorsv5.black,
  
          '&$checked': {
            color: colorsv5.primaryAccent
          }
        },
        checked: {},
        colorSecondary: {
          '&$checked': {
            color: colorsv5.primaryAccent
          }
        } 
      }
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 'auto',
          padding: 0,
  
          '&$expanded': {
            minHeight: 'auto'
          }
        },
        content: {
          alignItems: 'center',
          margin: 0,
          width: '95%',
          padding: `0 0 0 ${spacing * 9}px`,
  
          '&$expanded': {
            margin: 0
          }
        } 
      }
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: colorsv5.greyLight5,
          padding: spacing * 5,
          border: `1px solid ${colorsv5.greyLight}`,
          boxShadow: 'none',
  
          '&$expanded': {
            margin: 0,
  
            '&:last-child': {
              marginBottom: 'initial'
            }
          }
        },
        rounded: {
          borderRadius: containersRadius,
  
          '&::before': {
            display: 'none'
          }
        } 
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        // root: {
        //   lineHeight: 1,
        //   ...shortcutsv5.errorText
        // },
        contained: {
          marginLeft: 0,
          marginRight: 0
        } 
      }
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          flexWrap: 'nowrap',
          backgroundColor: 'white',
          padding: spacing * 4,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.16)'
        },
        message: {
          fontFamily,
          padding: 0,
          color: 'black',
          fontSize: 15,
          fontWeight: 500
        },
        action: {
          paddingLeft: spacing * 4
        } 
      }
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.16)'
        },
        elevation8: {
          boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.08)'
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          // Controls default (unchecked) color for the thumb
          color: colorsv5.greyLight,
        },
        colorPrimary: {
          "&.Mui-checked": {
            // Controls checked color for the thumb
            color: colorsv5.primaryAccent,
          }
        },
        track: {
          // Controls default (unchecked) color for the track
          // opacity: 0.2,
          // backgroundColor: colorsv5.greyLight2,
          ".Mui-checked.Mui-checked + &": {
            // Controls checked color for the track
            opacity: 0.8,
            backgroundColor: colorsv5.grey,
          }
        }
      }
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderRadius: containersRadius,
          overflow: 'hidden'
        } 
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&$hover:hover': {
            backgroundColor: alpha(colorsv5.primaryAccent, 0.04)
          }
        } 
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: `6px 0 6px ${spacing * 4}px`,
          color: colorsv5.primary,
          fontSize: 15,
          height: 54,
          borderColor: colorsv5.greyLight,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
  
          '&:last-of-type': {
            paddingRight: spacing * 4,
          },
  
          '& *': {
            whiteSpace: 'nowrap'
          }
        },
        head: {
          padding: `${spacing * 2}px ${spacing * 4}px`,
          backgroundColor: colorsv5.greyLight5,
          height: 34,
          borderColor: colorsv5.greyLight,
          borderStyle: 'solid',
          borderWidth: '1px 0 1px 0',
          color: colorsv5.greyDark,
          fontWeight: 400,
          whiteSpace: 'nowrap',
  
          '&:first-of-type': {
            borderTopLeftRadius: containersRadius
          },
  
          '&:last-child': {
            borderTopRightRadius: containersRadius
          }
        },
        stickyHeader: {
          backgroundColor: colorsv5.greyLight5
        } 
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: spacing * 4,
          overflow: 'hidden'
        },
        toolbar: {
          border: `1px solid ${colorsv5.greyLight}`,
          borderRadius: containersRadius,
          minHeight: 'auto',
          height: 42,
          backgroundColor: colorsv5.greyLight5
        } 
      }
    },
    // MuiPickersDay: {
    //   styleOverrides: {
    //     daySelected: {
    //       backgroundColor: colorsv5.primaryAccent,

    //       '&:hover': {
    //         backgroundColor: colorsv5.primaryAccent
    //       }
    //     },
    //     current: {
    //       color: colorsv5.primaryAccent
    //     }
    //   }
    // },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: colorsv5.greyDark
        }  
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: containersRadius,
          marginBottom: spacing,
  
          '&$selected': {
            backgroundColor: 'transparent',
  
            '&:hover': {
              backgroundColor: colorsv5.greyLight5
            }
          }
        } 
      }
    },
    MuiListItem: {
      styleOverrides: {
        gutters: {
          paddingLeft: spacing * 2,
          paddingRight: spacing * 2
        }
      }
    },
    MuiListItemButton: {
      defaultProps: {
        disableGutters: true,
        disableRipple: true,
      },
    },
    MuiToolbar: {
      defaultProps: {
        disableGutters: true,
      }
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          maxWidth: `calc(100% - ${spacing * 4}px)`,
          margin: `0 ${spacing * 2}px`
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: colorsv5.primaryAccent,
  
          '&$checked': {
            color: colorsv5.primaryAccent
          }
        },
        checked: {},
        colorSecondary: {
          '&$checked': {
            color: colorsv5.primaryAccent
          }
        }  
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colorsv5.greyLight5,
          border: `1px solid ${colorsv5.greyLight}`,
          boxShadow: 'none',
          borderRadius: containersRadius,
          // padding: spacing * 6
        } 
      }
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingTop: spacing * 5,
          display: 'block'
        }  
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${spacing * 2}px ${spacing * 5}px ${spacing * 5}px`
        }  
      }
    },
    MuiTooltip: {
      defaultProps: {
        placement: 'top',
        TransitionComponent: Fade,
        TransitionProps: {
          exit: false
        },
        leaveDelay: 0,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#000'
        }  
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing * 6
        }          
      }
    }
  }
});
